import React from 'react'
import {
  BooleanInput,
  DeleteButton,
  Edit,
  FormDataConsumer,
  NumberInput,
  required,
  SelectInput,
  SimpleForm,
  TextInput,
} from 'react-admin'
import { Box, Toolbar } from '@mui/material'
import ConditionsPanel from '../../Components/MlPricingRules/ConditionsPanel'
import PriceImpactPreview from '../../Components/MlPricingRules/PriceImpactPreview'
import ConfirmButton from '../../Components/Buttons/ConfirmButton'

const operationChoices = [
  { id: 'add', name: 'Add (+)' },
  { id: 'subtract', name: 'Subtract (-)' },
  { id: 'multiply', name: 'Multiply (×)' },
  { id: 'set', name: 'Set Ratio (=)' },
  { id: 'set_price_to', name: 'Set Price (=)' },
  { id: 'foxcub_override', name: 'Foxcub Override' },
  { id: 'add_fixed_value', name: 'Add Fixed Value (+)' },
  { id: 'subtract_fixed_value', name: 'Subtract Fixed Value (-)' },
]

const typeChoices = [
  { id: 'ml_guardrail', name: 'ML Guardrail' },
  { id: 'foxcub_override', name: 'Foxcub Override' },
  { id: 'special_case', name: 'Special Case' },
]

const CONFIRM_DESCRIPTION = [
  <>
    Please confirm you have reviewed all band adjustments and verified the
    expected output prices using the <strong>Price Impact Preview</strong> panel
    before saving.
  </>,
  'Once saved, this rule will affect live pricing calculations.',
]

const SaveToolBar = () => (
  <Toolbar style={{ display: 'flex', justifyContent: 'space-between' }}>
    <ConfirmButton
      title="Confirm Rule Configuration"
      description={CONFIRM_DESCRIPTION}
      cancelLabel="Go back and check"
      confirmLabel="Save rule"
    />
    <DeleteButton />
  </Toolbar>
)

const EditMlPricingRule = (props) => {
  return (
    <Edit {...props}>
      <SimpleForm toolbar={<SaveToolBar />}>
        <TextInput
          source="name"
          label="Rule Name"
          validate={required()}
          fullWidth
        />
        <TextInput
          source="description"
          label="Description"
          multiline
          fullWidth
        />
        <SelectInput
          source="ruleType"
          label="Rule Type"
          choices={typeChoices}
          validate={required()}
          fullWidth
        />
        <SelectInput
          source="matchType"
          label="Match Type"
          choices={[
            { id: 'match_all', name: 'All conditions must match' },
            { id: 'match_any', name: 'Any condition will trigger' },
          ]}
          validate={required()}
          fullWidth
        />
        <NumberInput
          source="sortOrder"
          label="Sort Order"
          helperText="Lower values are applied first. Use gaps (10, 20, 30) to allow insertion later."
          validate={required()}
          fullWidth
        />
        <BooleanInput source="active" label="Active" fullWidth />
        <BooleanInput
          source="override"
          label="Override (apply exclusively, skip all other rules)"
          fullWidth
        />

        <Box
          sx={{
            display: 'flex',
            gap: 3,
            width: '100%',
            alignItems: 'flex-start',
          }}
        >
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <h3>Band Adjustments</h3>
            <p>Leave an operation empty to pass that band through unchanged.</p>

            <SelectInput
              source="lowOperation"
              label="Low (Guide Price) — Operation"
              choices={operationChoices}
              resettable
              fullWidth
            />
            <NumberInput source="lowValue" label="Low — Value" fullWidth />

            <SelectInput
              source="mediumOperation"
              label="Medium (Target Price) — Operation"
              choices={operationChoices}
              resettable
              fullWidth
            />
            <NumberInput
              source="mediumValue"
              label="Medium — Value"
              fullWidth
            />

            <SelectInput
              source="highOperation"
              label="High (Max Price) — Operation"
              choices={operationChoices}
              resettable
              fullWidth
            />
            <NumberInput source="highValue" label="High — Value" fullWidth />

            <ConditionsPanel />
          </Box>
          <FormDataConsumer>
            {({ formData }) => (
              <Box sx={{ width: 340, flexShrink: 0, pt: '56px' }}>
                <PriceImpactPreview
                  lowOperation={formData.lowOperation}
                  lowValue={formData.lowValue}
                  mediumOperation={formData.mediumOperation}
                  mediumValue={formData.mediumValue}
                  highOperation={formData.highOperation}
                  highValue={formData.highValue}
                />
              </Box>
            )}
          </FormDataConsumer>
        </Box>
      </SimpleForm>
    </Edit>
  )
}

export default EditMlPricingRule
